/**
 * @name FloatingLabelInput
 * @extends HTMLElement
 * @desc Base class for floating labels.
 * @author kari
 * @example
 * ```
 * <floating-label-input>
 *  <label class="control-label">My Label</label>
 *  <input class="form-control" type="text">
 * </floating-label-input>
 * ```
 */

let FloatingLabel = Object.create(HTMLElement.prototype);

FloatingLabel.createdCallback = function createdCallback() {
    // selector cache
    this.$ = {};

    this.classnames = {
        isFocused: 'is-focused',
        isReady: 'floating-label',
        hasValue: 'is-not-empty',
        isEmpty: 'is-empty',
    };

    this.state = {
        isReady: false,
        isEmpty: false,
        isFocused: false,
    };
};

FloatingLabel.attachedCallback = function attachedCallback() {
    this.$.label = this.querySelector('.control-label');
    this.$.input = this.querySelector('.form-control');
    this.state.isReady = !!(this.$.label && this.$.input);

    if (!this.state.isReady) {
        return;
    }

    // bind event handlers to the instance
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleValue = this.handleValue.bind(this);

    this.handleValue();
    this.addEventListener('click', this.handleClick);
    this.$.input.addEventListener('blur', this.handleBlur);
    this.$.input.addEventListener('change', this.handleValue);
    this.$.input.addEventListener('input', this.handleValue);

    this.classList.add(this.classnames.isReady);
};

FloatingLabel.detachedCallback = function detachedCallback() {
    if (this.state.isReady) {
        this.removeEventListener('click', this.handleClick);
        this.$.input.removeEventListener('blur', this.handleBlur);
        this.$.input.removeEventListener('change', this.handleValue);
        this.$.input.removeEventListener('input', this.handleValue);
        this.$ = {};
    }
};

FloatingLabel.handleBlur = function handleBlur() {
    this.state.isFocused = false;
    this.classList.remove(this.classnames.isFocused);
};

FloatingLabel.handleFocus = function handleFocus() {
    this.state.isFocused = true;
    this.classList.add(this.classnames.isFocused);
};

FloatingLabel.handleClick = function handleClick(e) {
    // [CHK-140] Do not focus if a feedback popover icon has been clicked.
    if (!e.target.dataset.toggle) {
        this.$.input.focus();
    }
};

FloatingLabel.handleValue = function handleValue() {
    this.state.isEmpty = this.$.input.value === '';

    if (this.state.isEmpty) {
        this.classList.remove(this.classnames.hasValue);
        this.classList.add(this.classnames.isEmpty);
    } else {
        this.handleFocus();
        this.classList.add(this.classnames.hasValue);
        this.classList.remove(this.classnames.isEmpty);
    }
};

export default FloatingLabel;
